(() => {
  const HEADER_SELECTOR = "[data-el-header]";
  const CART_EVENTS = [
    "cart:updated",
    "cart:refresh",
    "cartQuantityUpdated",
    "product:added",
    "ajaxProduct:added",
  ];

  function initAllHeaders() {
    document.querySelectorAll(HEADER_SELECTOR).forEach(initHeader);
  }

  function initShopMenu(menu) {
    const trigger = menu.querySelector("[data-el-shop-trigger]");
    const panel = menu.querySelector("[data-el-shop-panel]");
    if (!trigger || !panel) return;

    const desktop = window.matchMedia("(min-width: 1100px)");
    const links = Array.from(panel.querySelectorAll("a[href]"));
    const keySteps = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 };
    let closeTimer = null;
    let openedByHover = false;

    const isOpen = () => trigger.getAttribute("aria-expanded") === "true";

    function open(byHover) {
      window.clearTimeout(closeTimer);
      if (isOpen()) {
        if (!byHover) openedByHover = false;
        return;
      }
      openedByHover = byHover;
      trigger.setAttribute("aria-expanded", "true");
    }

    function close(returnFocus) {
      window.clearTimeout(closeTimer);
      if (!isOpen()) return;
      openedByHover = false;
      trigger.setAttribute("aria-expanded", "false");
      if (returnFocus) trigger.focus();
    }

    menu.addEventListener("pointerenter", (event) => {
      if (event.pointerType === "mouse" && desktop.matches) open(true);
    });

    menu.addEventListener("pointerleave", (event) => {
      if (event.pointerType !== "mouse" || !openedByHover) return;
      window.clearTimeout(closeTimer);
      closeTimer = window.setTimeout(() => close(false), 250);
    });

    trigger.addEventListener("click", (event) => {
      if (isOpen() && openedByHover && event.detail !== 0) return;
      if (isOpen()) {
        close(false);
      } else {
        open(false);
      }
    });

    trigger.addEventListener("keydown", (event) => {
      if (event.key !== "ArrowDown" || !links.length) return;
      event.preventDefault();
      open(false);
      links[0].focus();
    });

    panel.addEventListener("keydown", (event) => {
      const index = links.indexOf(document.activeElement);
      if (index === -1) return;
      let next = null;
      if (event.key in keySteps) {
        next = (index + keySteps[event.key] + links.length) % links.length;
      } else if (event.key === "Home") {
        next = 0;
      } else if (event.key === "End") {
        next = links.length - 1;
      }
      if (next === null) return;
      event.preventDefault();
      links[next].focus();
    });

    menu.addEventListener("focusout", (event) => {
      if (!isOpen() || !document.hasFocus()) return;
      if (!menu.contains(event.relatedTarget)) close(false);
    });

    document.addEventListener("keydown", (event) => {
      if (event.key === "Escape" && isOpen()) {
        close(menu.contains(document.activeElement));
      }
    });

    document.addEventListener("click", (event) => {
      if (isOpen() && !menu.contains(event.target)) close(false);
    });

    desktop.addEventListener("change", () => close(false));
    window.addEventListener("pageshow", () => close(false));
  }

  function initHeader(sectionRoot) {
    if (!sectionRoot || sectionRoot.dataset.initialized === "true") return;
    sectionRoot.dataset.initialized = "true";

    const body = document.body;
    const rootUrl = sectionRoot.dataset.rootUrl || "/";

    const overlay = sectionRoot.querySelector("[data-el-overlay]");
    const menuDrawer = sectionRoot.querySelector("[data-el-menu-drawer]");
    const menuToggle = sectionRoot.querySelector("[data-el-menu-toggle]");
    const closeButtons = sectionRoot.querySelectorAll("[data-el-close]");
    const cartCount = sectionRoot.querySelector("[data-el-cart-count]");

    const fixedHeader = sectionRoot.querySelector(".el-fixed-header");
    let scrollTicking = false;

    const isTransparent = sectionRoot.hasAttribute("data-el-transparent");
    const scrollThreshold = isTransparent
      ? parseInt(sectionRoot.dataset.elScrollThreshold || "60", 10)
      : 0;

    function updateHeaderAppearance() {
      if (!fixedHeader) return;

      const isPastThreshold = window.scrollY > scrollThreshold;
      fixedHeader.classList.toggle("is-scrolled", isPastThreshold);
    }

    function onScroll() {
      if (scrollTicking) return;

      scrollTicking = true;
      window.requestAnimationFrame(() => {
        updateHeaderAppearance();
        scrollTicking = false;
      });
    }

    let activeDrawer = null;

    function openMenu() {
      if (!menuDrawer || !overlay) return;

      closeMenu(false);

      menuDrawer.classList.add("is-open");
      menuDrawer.setAttribute("aria-hidden", "false");

      if (isTransparent && fixedHeader) {
        fixedHeader.classList.add("is-scrolled");
      }

      overlay.classList.add("is-open");
      body.classList.add("el-fixed-drawer-open");

      if (menuToggle) {
        menuToggle.setAttribute("aria-expanded", "true");
      }

      activeDrawer = menuDrawer;
    }

    function closeMenu(resetFocus = true) {
      if (menuDrawer) {
        menuDrawer.classList.remove("is-open");
        menuDrawer.setAttribute("aria-hidden", "true");
      }

      if (overlay) {
        overlay.classList.remove("is-open");
      }

      body.classList.remove("el-fixed-drawer-open");

      if (menuToggle) {
        menuToggle.setAttribute("aria-expanded", "false");
      }

      if (resetFocus && activeDrawer === menuDrawer && menuToggle) {
        menuToggle.focus();
      }

      if (isTransparent) {
        updateHeaderAppearance();
      }

      activeDrawer = null;
    }

    async function getCart() {
      const response = await fetch(`${rootUrl}cart.js`, {
        credentials: "same-origin",
        headers: {
          Accept: "application/json",
        },
      });

      if (!response.ok) {
        throw new Error("Failed to fetch cart");
      }

      return response.json();
    }

    function updateCartCount(cart) {
      if (!cartCount) return;

      const count = Number(cart?.item_count || 0);

      if (count > 0) {
        cartCount.textContent = String(count);
        cartCount.classList.remove("is-hidden");
        cartCount.removeAttribute("hidden");
        cartCount.setAttribute("aria-hidden", "false");
      } else {
        cartCount.textContent = "";
        cartCount.classList.add("is-hidden");
        cartCount.setAttribute("hidden", "");
        cartCount.setAttribute("aria-hidden", "true");
      }
    }

    function refreshCartCount() {
      if (!cartCount) return;

      getCart()
        .then(updateCartCount)
        .catch(() => {});
    }

    if (menuToggle) {
      menuToggle.addEventListener("click", () => {
        const isOpen = menuDrawer?.classList.contains("is-open");
        if (isOpen) {
          closeMenu();
        } else {
          openMenu();
        }
      });
    }

    closeButtons.forEach((button) => {
      button.addEventListener("click", () => closeMenu());
    });

    if (overlay) {
      overlay.addEventListener("click", () => closeMenu());
    }

    document.addEventListener("keydown", (event) => {
      if (event.key === "Escape" && activeDrawer === menuDrawer) {
        closeMenu();
      }
    });

    CART_EVENTS.forEach((eventName) => {
      document.addEventListener(eventName, refreshCartCount);
    });

    window.addEventListener("pageshow", refreshCartCount);

    const shopMenu = sectionRoot.querySelector("[data-el-shop-menu]");
    if (shopMenu) {
      initShopMenu(shopMenu);
    }

    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", updateHeaderAppearance);
    window.addEventListener("pageshow", updateHeaderAppearance);

    updateHeaderAppearance();

    refreshCartCount();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initAllHeaders);
  } else {
    initAllHeaders();
  }

  document.addEventListener("shopify:section:load", initAllHeaders);
})();
